import empty from "is-empty";

import { Availability } from "./types";
import { FETCH_STATES } from "src/redux/Tx/constants";
import Math from "src/utils/math";
import {
    getTermFromDate,
    getDateFromTerm,
    getDefaultTxPurpose,
    getMaximumPossibleMaturityDate,
} from "src/utils";
import { TX_PURPOSE_LABELS } from "src/redux/Confirmations/constants";
import { SettingsSelectors } from "src/redux/Settings/selectors";
export const availabilityId = (availability) =>
    `${availability.lender?.name}-${availability.id}`;

const defaultState = {
    todayAvailability: [],
    selectedAvailability: [],
    requestRows: [],
    hasMarkitAccess: false,
    pastedNeeds: [],
    takenNeedIds: [],
};

const updateAvailability = (newList, oldList) => {
    const newIds = newList.map((l) => availabilityId(l));
    const list = oldList.filter((l) => !newIds.includes(availabilityId(l)));
    return list.concat(newList);
};

export default (state = defaultState, action) => {
    switch (action.type) {
        case Availability.NEW_AVAILABILITY_LIST:
            return {
                ...state,
                todayAvailability: action.availability,
            };

        case Availability.DELETE_AVAILABILITY_LIST:
            const ids = action.entries.map((e) => availabilityId(e));
            return {
                ...state,
                todayAvailability: state.todayAvailability.filter(
                    (availability) => {
                        return !ids.includes(availabilityId(availability));
                    }
                ),
            };

        case Availability.UPDATE_AVAILABILITY_LIST:
            return {
                ...state,
                todayAvailability: updateAvailability(
                    action.entries,
                    state.todayAvailability
                ),
            };

        case Availability.SET_SELECTED_AVAILABILITY:
            return {
                ...state,
                selectedAvailability: [...action.rows],
            };

        case Availability.PREPARE_TAKE:
            return {
                ...state,
                requestRows: state.selectedAvailability.map((row) => ({
                    ...row,
                    Quantity: row.AvailableQuantity,
                    Rate: row.AvailableRate,
                    Amount: row.AvailableValue,
                    Maturity: action.enable90DayMaxMaturity
                        ? undefined
                        : row.Maturity || new Date("9999-12-31"),
                    Purpose:
                        row.Country === "KR"
                            ? TX_PURPOSE_LABELS.SHORT_SELLING.value
                            : TX_PURPOSE_LABELS.NONE.value,
                    marketData: {
                        status: FETCH_STATES.FETCHING,
                    },
                })),
            };

        case Availability.PREPARE_NEEDS:
            return {
                ...state,
                requestRows: action.newAvailability.map((row) => ({
                    ...row,
                    Maturity: action.enable90DayMaxMaturity
                        ? undefined
                        : row.Maturity || new Date("9999-12-31"),
                })),
            };

        case Availability.RESET_TAKE:
            return {
                ...state,
                selectedAvailability: [],
                requestRows: [],
                pastedNeeds: [],
            };
        case Availability.AVAILABILITY_SET_NEEDS:
            return { ...state, ...action };

        case Availability.ATTACH_MARKET_DATA:
            const marketData = action.marketData;
            const mdIndex = action.listTicker;

            let hasMarkitAccess = false;
            let newRows = state.requestRows.map((row, index) => {
                const stockId = row.SecurityId || row.ISIN;

                if (!mdIndex.includes(stockId)) return row;

                const stockData = marketData[stockId]?.data;
                if (stockData) {
                    if (index === 0) {
                        hasMarkitAccess = !empty(stockData.referenceRate);
                    }

                    const TradeableDates = stockData.tradableDates.map((s) =>
                        s.replace(/-/g, "")
                    );
                    return {
                        ...row,
                        marketData: {
                            status: FETCH_STATES.FETCHED,
                            data: stockData,
                            error: null,
                        },
                        hasError: false,
                        TradeableDates,
                        Utilization: stockData.utilization?.markit?.value,
                        BenchmarkFee: stockData.referenceRate?.markit?.value,
                        Purpose: getDefaultTxPurpose(stockData),
                        maxMaturityDates: stockData.maxMaturityDates,
                        Term: getTermFromDate(row.ValueDate, row.Maturity),
                    };
                }

                return {
                    ...row,
                    marketData: {
                        status: FETCH_STATES.ERROR,
                        error: "Not Found",
                    },
                    SecurityId: row.SecurityId + " ",
                    hasError: true,
                };
            });

            return {
                ...state,
                requestRows: newRows,
                hasMarkitAccess,
            };

        case Availability.APPLY_REF_RATE:
            return {
                ...state,
                requestRows: state.requestRows.map((row) => ({
                    ...row,
                    Rate: row.BenchmarkFee
                        ? Math.chain(row.BenchmarkFee)
                              .divide(100)
                              .multiply(action.multiplier)
                              .done()
                        : row.Rate,
                })),
            };

        case Availability.APPLY_MATURITY:
            return {
                ...state,
                requestRows: state.requestRows.map((row) => {
                    if (action.maturity.type === "MAX") {
                        const maxMaturity = getMaximumPossibleMaturityDate(
                            row.Country,
                            row.Purpose,
                            row.ValueDate,
                            row.TradeableDates,
                            row.marketData.data.maxMaturityDates,
                            action.enable90DayMaxMaturity
                        );

                        return {
                            ...row,
                            Maturity: maxMaturity,
                            Term: getTermFromDate(row.ValueDate, maxMaturity),
                        };
                    }

                    return {
                        ...row,
                        Maturity: row.ValueDate
                            ? action.maturity.date ||
                              getDateFromTerm(
                                  row.ValueDate,
                                  action.maturity.term
                              )
                            : new Date("9999-12-31"),
                        Term: row.ValueDate
                            ? action.maturity.term ||
                              getTermFromDate(
                                  row.ValueDate,
                                  action.maturity.date
                              )
                            : "",
                    };
                }),
            };

        case Availability.UPDATE_REQUEST_ROWS:
            const idx = action.rows.map((r) => r.id);
            return {
                ...state,
                requestRows: state.requestRows.map((r) => {
                    const position = idx.indexOf(r.id);
                    if (position >= 0) return action.rows[position];
                    return r;
                }),
            };

        case Availability.DELETE_REQUEST_ROW:
            const index = state.requestRows
                .map((r) => r.id)
                .indexOf(action.rowId);
            const requestRows = [...state.requestRows];
            requestRows.splice(index, 1);
            return { ...state, requestRows };
        default:
            return state;
    }
};
